import { inject, Injectable } from "@angular/core";
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { CachedRepositoryService, NavigationService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, IGridViewMetadata } from "@tylertech/tyler-api-types";
import { from, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { ITableFilter, MetadataIntermediary } from "../../interfaces";
import
{
    ResetStateService,
    GridFilterService,
    InfoGridLayoutService,
    MetadataInputTypeHelperService,
    MetadataService,
    MetadataViewService,
    ProviderStorageService,
    GridProcessingService,
    GridSummaryService,
    GridFetchType,
    GridSortService,
    GridEntityService,
} from "../../services";

@Injectable()
export class GridLoadService extends ResetStateService
{
    private $providerStorageService = inject(ProviderStorageService);
    private $metadataService = inject(MetadataService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private $repositoryService = inject(CachedRepositoryService);
    private $metadataViewService = inject(MetadataViewService);
    private $gridEntityService = inject(GridEntityService);
    private $gridFilterService = inject(GridFilterService);
    private $gridLayoutService = inject(InfoGridLayoutService);
    private $gridProcessingService = inject(GridProcessingService);
    private $gridSortService = inject(GridSortService);
    private $gridSummaryService = inject(GridSummaryService);

    FilterChanged: Record<string, boolean> = {};

    constructor()
    {
        super(inject(NavigationService));
    }

    public getData(
        grid: IgxGridComponent,
        model: any,
        viewMetadata: IGridViewMetadata,
        applyPaging: boolean,
        afterFetch: (applyPaging: boolean) => void,
        skip?: number,
        take?: number,
        skipSort?: boolean,
        loadRecent?: boolean
    ): Subscription
    {
        const providerId = viewMetadata.GridProperties["$providerId"];

        this.$gridLayoutService.GridLayoutId[providerId] = viewMetadata.GridId;
        this.$gridSortService.initializeSort(providerId, skipSort, viewMetadata, grid);

        let forceRefetch = false;
        if (this.$providerStorageService.Refetch[providerId])
        {
            forceRefetch = this.$providerStorageService.Refetch[providerId] === true;
            this.$providerStorageService.Refetch[providerId] = false;
        }

        return from(this.fetchEntities(grid, providerId, model, viewMetadata, skip, take, forceRefetch, loadRecent)
            .then(entities => this.prepareEntities(entities, grid, providerId, viewMetadata, skip, take, forceRefetch)))
            .pipe(
                map(() =>
                {
                    applyPaging = false;
                })
            )
            .subscribe(() =>
            {
                if (afterFetch) afterFetch(applyPaging);
            });
    }

    fetchEntities(grid: IgxGridComponent, providerId: string, model: any, viewMetadata: IGridViewMetadata, skip: number, take: number, forceRefetch: boolean, loadRecent: boolean): Promise<any[]>
    {
        const self = this;
        const recentIds = (loadRecent && this.$gridFilterService.allowShowRecent(viewMetadata)) ? this.$gridFilterService.recentIds(viewMetadata.GridId) : undefined;

        if (this.$providerStorageService.GridFetchType[providerId] === GridFetchType.ServerStore)
        {
            if (!self.$providerStorageService.Scrolling[providerId])
                this.$providerStorageService.GridData[providerId] = undefined;
            const odataFilterBuilder = this.$gridFilterService.oDataFilterBuilder(providerId, viewMetadata, skip, take, true, model);
            return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
                .then(filter =>
                {
                    if (filter)
                        odataFilterBuilder.constantFilter(filter.replace("$filter=", ""));
                    return this.$repositoryService
                        .Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, odataFilterBuilder.build())
                        .then(results =>
                        {
                            const result = Array.isArray(results) ? results[0] : results;
                            self.$providerStorageService.TotalHits[providerId] = result["@odata.count"];
                            return result["value"];
                        });
                });

        }
        else if (this.$providerStorageService.GridFetchType[providerId] === GridFetchType.LocalStore && forceRefetch)
        {
            const odataFilter = this.$metadataViewService.createFilterString(model, viewMetadata);
            return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
                .then(filter =>
                {
                    return self.$repositoryService.Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, combineFilter(odataFilter, filter));
                });
        }
        return Promise.resolve(this.$providerStorageService.LocalEntitiesStore[providerId]);

        function combineFilter(odataFilter: string, filter: string): string
        {
            if (Utilities.IsNullOrUndefined(odataFilter))
                odataFilter = "";
            if (Utilities.IsNullOrUndefined(filter))
                filter = "";
            const combinedFilter = odataFilter + filter;
            if (combinedFilter === "")
                return null;
            return combinedFilter;
        }
    }

    prepareEntities(entities: any[], grid: IgxGridComponent, providerId: string, viewMetadata: IGridViewMetadata, skip: number, take: number, forceRefetch: boolean): Promise<any>
    {
        const self = this;
        const tableFilter = <ITableFilter>{
            Top: take,
            OrderBy: this.$gridSortService.fetchOrder(providerId),
            Skip: skip,
            Search: grid.filteringExpressionsTree.filteringOperands,
            Properties: viewMetadata.GridProperties.ClassMetadata.Properties,
            GroupedColumns: grid.groupingExpressions?.map(g => g.fieldName)
        };

        //Keep already-loaded (infinite scrolled) rows on a refetch, but never impose a limit when the caller asked for everything (no take)
        if (!Utilities.IsNullOrUndefined(tableFilter.Top) && self.$providerStorageService.GridData[providerId]?.length > tableFilter.Top)
        {
            tableFilter.Top = self.$providerStorageService.GridData[providerId].length;
        }

        if (this.$providerStorageService.GridFetchType[providerId] === GridFetchType.ServerStore)
        {
            return this.$gridProcessingService.buildGridRows(entities, viewMetadata, null)
                .then(rows =>
                {
                    if (this.$providerStorageService.Scrolling[providerId] && this.$providerStorageService.GridData[providerId])
                        this.$providerStorageService.GridData[providerId] = [...this.$providerStorageService.GridData[providerId], ...rows];
                    else
                        this.$providerStorageService.GridData[providerId] = rows;
                    this.$gridEntityService.syncPaged(providerId, viewMetadata);
                });
        }
        else if (this.$providerStorageService.GridFetchType[providerId] === GridFetchType.LocalStore && forceRefetch)
        {
            return self.augmentAndPopulateLocalStore(providerId, viewMetadata.GridProperties.ClassMetadata, entities, viewMetadata)
                .then(() => self.localDataGridLoad(grid, tableFilter, providerId, viewMetadata));
        }
        return this.localDataGridLoad(grid, tableFilter, providerId, viewMetadata);
    }

    localDataGridLoad(grid: IgxGridComponent, tableFilter: ITableFilter, providerId: string, viewMetadata: IGridViewMetadata): Promise<any>
    {
        const self = this;
        return self.ApplyFilterSort(grid, tableFilter, providerId, viewMetadata)
            .then(filteredEntities =>
            {
                self.$providerStorageService.TotalHits[providerId] = filteredEntities.filter(x => x.SerializedRowState !== "Deleted").length;
                const pagedEntities = self.getPagedEntities(providerId, tableFilter, filteredEntities);

                return self.$gridProcessingService.buildGridRows(pagedEntities, viewMetadata, viewMetadata.Actions)
                    .then(rows =>
                    {
                        if (self.$providerStorageService.Scrolling[providerId] && self.$providerStorageService.GridData[providerId])
                            self.$providerStorageService.GridData[providerId] = [...self.$providerStorageService.GridData[providerId], ...rows];
                        else
                            self.$providerStorageService.GridData[providerId] = rows;
                        buildColumnSummaries();
                    });
            });

        function buildColumnSummaries(): void
        {
            if (!self.$providerStorageService.BuildSummaries[providerId])
                return;
            self.$providerStorageService.ColumnSummaries[providerId] = {};
            self.$gridSummaryService.setColumnSummaries(providerId, viewMetadata);
            self.$providerStorageService.BuildSummaries[providerId] = false;
        }
    }

    setGridFetchType(viewMetadata: IGridViewMetadata, providerId: string): void
    {
        if (!viewMetadata?.GridProperties)
            return;
        if (viewMetadata.SubPropertyName)
            this.$providerStorageService.GridFetchType[providerId] = GridFetchType.SubProperty;
        else if (viewMetadata.GridProperties.ServerPaging)
            this.$providerStorageService.GridFetchType[providerId] = GridFetchType.ServerStore;
        else
            this.$providerStorageService.GridFetchType[providerId] = GridFetchType.LocalStore;
    }

    ApplyFilterSort(grid: IgxGridComponent, tableFilter: ITableFilter, providerId: string, viewMetadata: IGridViewMetadata): Promise<any[]>
    {
        const self = this;
        let includeLookups: string[] = [];
        let hasFilter = false;
        let filteredColumns: string[];

        if ((tableFilter.Search && tableFilter.Search.length > 0) || tableFilter.OrderBy || tableFilter.GroupedColumns?.length > 0)
        {
            const filteredEntities = self.$providerStorageService.FilteredEntities[providerId];
            if (!this.FilterChanged[providerId] && filteredEntities?.length > 0)
                return Promise.resolve(filteredEntities);
            hasFilter = true;
            filteredColumns = tableFilter.Search.map(expressionTree => expressionTree.fieldName);
            if (tableFilter.OrderBy)
            {
                const sorts = tableFilter.OrderBy.split(",");
                filteredColumns.push(...sorts.map(x => x.split(" ")[0]));
            }
            if (tableFilter.GroupedColumns?.length > 0)
                filteredColumns.push(...tableFilter.GroupedColumns);
            const intermediaries = this.$metadataService.FlattenProperties(viewMetadata.GridProperties.ClassMetadata, null, {});
            includeLookups = intermediaries.filter(p => this.$metadataInputTypeHelperService.isLookup(p.property.InputType)).map(x => x.name);

            filteredColumns = updateParent(filteredColumns, intermediaries);
            includeLookups = updateParent(includeLookups, intermediaries);
        }

        this.FilterChanged[providerId] = false;

        if (!hasFilter)
        {
            self.$providerStorageService.LocalEntitiesStore[providerId].forEach(cachedEntity => cachedEntity["$inFilter"] = true);
            self.$providerStorageService.FilteredEntities[providerId] = self.$providerStorageService.LocalEntitiesStore[providerId];
            return Promise.resolve(self.$providerStorageService.LocalEntitiesStore[providerId]);
        }

        return this.$gridProcessingService.buildGridRows(this.$providerStorageService.LocalEntitiesStore[providerId], viewMetadata, viewMetadata.Actions, filteredColumns, includeLookups, hasFilter)
            .then(rows =>
            {
                const sortedRows = self.$gridSortService.sort(providerId, rows, viewMetadata);
                const sortedEntities = sortedRows.map(x => x["$$model"]);

                const orderByEntity = new Map<any, number>();
                sortedEntities.forEach((entity, i) => orderByEntity.set(entity, i));
                this.$providerStorageService.LocalEntitiesStore[providerId].sort((a, b) => (orderByEntity.get(a) ?? -1) - (orderByEntity.get(b) ?? -1));

                const filteredRows = self.$gridFilterService.filterLocalTable(rows, grid);
                const filteredEntities = self.$gridSortService.sort(providerId, filteredRows, viewMetadata).map(x => x["$$model"]);
                self.$providerStorageService.LocalEntitiesStore[providerId].forEach(cachedEntity => cachedEntity["$inFilter"] = false);
                filteredEntities.forEach(cachedEntity => cachedEntity["$inFilter"] = true);
                self.$providerStorageService.FilteredEntities[providerId] = filteredEntities;
                return filteredEntities;
            });

        function updateParent(columns: string[], intermediaries: MetadataIntermediary[]): string[]
        {
            return columns.map(col =>
            {
                const intermediary = intermediaries.find(i => i.name === col);
                if (intermediary?.property["$parentPropertyName"])
                    return `${intermediary.property["$parentPropertyName"]}.${col}`;
                return col;
            });
        }
    }

    augmentAndPopulateLocalStore(
        key: string,
        metadata: ClassMetadata,
        entities: any[],
        viewMetadata: IGridViewMetadata
    ): Promise<any>
    {
        return this.$metadataService.AugmentModels(metadata, entities, viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected")
            .then(() =>
            {
                this.$providerStorageService.LocalEntitiesStore[key] = entities;
                this.$providerStorageService.FilteredEntities[key] = [];
            });
    }

    getPagedEntities(key: string, filter: ITableFilter, entities?: any[]): any[]
    {
        if (Utilities.IsNullOrUndefined(entities))
            entities = this.$providerStorageService.LocalEntitiesStore[key].filter(x => x.SerializedRowState !== "Deleted");
        else
            entities = entities.filter(x => x.SerializedRowState !== "Deleted");
        entities = topSkip(entities, filter.Top, filter.Skip)();
        return entities;

        function topSkip<T>(entities: Array<T>, top: number, skip: number): () => Array<T>
        {
            const l = entities.length;
            if (!skip)
            {
                skip = 0;
            }

            if (!top)
            {
                top = l;
            }

            return () =>
            {
                const end = skip + top;
                const part = entities.slice(skip, end);
                skip = end < l ? end : 0;
                return part;
            };
        }
    }
}
